import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  FilePlus2,
  FileText,
  Hourglass,
  ListTodo,
  Lock,
  PencilLine,
  BadgeCheck,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  activityCodesQuery,
  branchesQuery,
  itemsQuery,
  organizationQuery,
  orgCurrenciesQuery,
  partiesQuery,
  salesListQuery,
  taxesQuery,
} from '../../api/queries';
import { useAccess } from '../../app/access';
import { useI18n } from '../../i18n/I18nProvider';
import { UserText } from '../../ui/Bidi';
import { Button } from '../../ui/Button';
import { Badge, Skeleton } from '../../ui/Feedback';
import { Code, DescriptionList, Money, Page, Panel, useDocumentTitle } from '../../ui/Layout';
import { QueryError } from '../../ui/QueryError';
import { ItemDialog } from '../items/ItemDialog';
import { CustomerDialog } from '../parties/CustomerDialog';
import styles from '../sales/Sales.module.css';
import { DocStatusBadge, MockLabel, TaxStatusBadge, taxAxisOf } from '../sales/status';
import {
  ActivityDialog,
  BranchDialog,
  CurrencyDialog,
  ProfileDialog,
  TaxDialog,
} from './SetupDialogs';

type Step = 'rin' | 'profile' | 'branch' | 'activity' | 'tax' | 'customer' | 'item';

/** The Today hub (IA.md §1): what needs attention now, the setup checklist, and the organization. */
export function OrgHomePage() {
  const { t, formatDate } = useI18n();
  const { orgId = '' } = useParams({ strict: false });
  const navigate = useNavigate();
  const access = useAccess(orgId);
  const org = useQuery(organizationQuery(orgId));
  useDocumentTitle(org.data?.name ?? t.nav.today);
  const [dialog, setDialog] = useState<Step | 'currency' | null>(null);

  const canInvoices = access.can('invoice.view');
  const kpi = (q: Parameters<typeof salesListQuery>[1]) => ({
    ...salesListQuery(orgId, { ...q, limit: 1 }),
    enabled: canInvoices,
  });
  const drafts = useQuery(kpi({ tab: 'invoices', status: 'draft' }));
  const sending = useQuery(kpi({ tab: 'invoices', taxStatus: 'submitting,submitted' }));
  const valid = useQuery(kpi({ tab: 'invoices', taxStatus: 'valid' }));
  // WP-1b.2: a superseded document (its correction was issued) no longer needs attention.
  const attention = useQuery(
    kpi({ tab: 'invoices', status: 'issued,signed', taxStatus: 'invalid,rejected,not_accepted' }),
  );
  const recent = useQuery({
    ...salesListQuery(orgId, { sort: 'updated', dir: 'desc', limit: 5 }),
    enabled: canInvoices,
  });

  const branches = useQuery(branchesQuery(orgId));
  const activities = useQuery(activityCodesQuery(orgId));
  const taxes = useQuery(taxesQuery(orgId));
  const currencies = useQuery(orgCurrenciesQuery(orgId));
  const customers = useQuery({
    ...partiesQuery(orgId, { role: 'customer', limit: 1 }),
    enabled: access.can('customer.view'),
  });
  const items = useQuery({ ...itemsQuery(orgId, { limit: 1 }), enabled: access.can('item.view') });

  const done: Record<Step, boolean> = {
    rin: Boolean(org.data?.rin),
    profile: Boolean(org.data?.email && org.data?.phone),
    branch: (branches.data?.length ?? 0) > 0,
    activity: (activities.data?.length ?? 0) > 0,
    tax: (taxes.data?.length ?? 0) > 0,
    customer: (customers.data?.total ?? 0) > 0,
    item: (items.data?.total ?? 0) > 0,
  };
  const steps: Step[] = ['rin', 'profile', 'branch', 'activity', 'tax', 'customer', 'item'];
  const remaining = steps.filter((s) => !done[s]).length;
  const canSettings = access.can('settings.manage');
  const role = access.role ? (t.roles[access.role as keyof typeof t.roles] ?? access.role) : null;

  return (
    <Page>
      <section className={styles.hero}>
        <div className={styles.heroRow}>
          <div>
            <span className={styles.heroEyebrow}>
              <ListTodo size={14} aria-hidden="true" />
              {t.nav.today}
            </span>
            <h1 className={styles.heroTitle}>
              {org.data ? (
                <UserText>{org.data.name}</UserText>
              ) : (
                <Skeleton width="16rem" height="2rem" />
              )}
            </h1>
            <div className={styles.headerBadges}>
              {org.data ? (
                <Badge tone={org.data.etaEnvironment === 'production' ? 'accent' : 'neutral'}>
                  {t.env[org.data.etaEnvironment]}
                </Badge>
              ) : null}
              {role ? <Badge tone="primary">{role}</Badge> : null}
              <MockLabel environment={org.data?.etaEnvironment} />
            </div>
          </div>
          {access.can('invoice.create') ? (
            <div className={styles.heroActions}>
              <Button
                variant="secondary"
                className={styles.heroButton}
                icon={<FilePlus2 size={16} />}
                testId="dashboard-new-invoice"
                onPress={() =>
                  void navigate({ to: '/orgs/$orgId/sales/invoices/new', params: { orgId } })
                }
              >
                {t.dashboard.newInvoice}
              </Button>
            </div>
          ) : null}
        </div>
      </section>
      {org.error ? <QueryError error={org.error} onRetry={() => void org.refetch()} /> : null}

      {canInvoices && org.data && org.data.etaEnvironment !== 'production' ? (
        <p className={styles.kpiNote} data-testid="kpi-mock-note">
          <MockLabel environment={org.data.etaEnvironment} /> {t.sales.kpis.mockNote}
        </p>
      ) : null}
      {canInvoices ? (
        <div className={styles.cardGrid} data-testid="kpis">
          <Kpi
            tone={styles.toneInfo}
            icon={<PencilLine size={15} />}
            label={t.sales.kpis.drafts}
            value={drafts.data?.total}
            to={{ status: 'draft' }}
            orgId={orgId}
            testId="kpi-drafts"
          />
          <Kpi
            tone={styles.toneWarning}
            icon={<Hourglass size={15} />}
            label={t.sales.kpis.sending}
            value={sending.data?.total}
            to={{ taxStatus: 'submitting,submitted' }}
            orgId={orgId}
            testId="kpi-sending"
          />
          <Kpi
            tone={styles.toneSuccess}
            icon={<BadgeCheck size={15} />}
            label={t.sales.kpis.valid}
            value={valid.data?.total}
            to={{ taxStatus: 'valid' }}
            orgId={orgId}
            testId="kpi-valid"
          />
          <Kpi
            tone={styles.toneDanger}
            icon={<AlertTriangle size={15} />}
            label={t.sales.kpis.attention}
            value={attention.data?.total}
            to={{ status: 'issued,signed', taxStatus: 'invalid,rejected,not_accepted' }}
            orgId={orgId}
            testId="kpi-attention"
          />
        </div>
      ) : null}

      <div className={styles.editorGrid}>
        <div className={styles.stack}>
          {canInvoices ? (
            <section className={styles.card} data-testid="recent">
              <div className={styles.cardHead}>
                <h2 className={styles.cardTitle}>
                  <span className={styles.cardIcon} aria-hidden="true">
                    <FileText size={16} />
                  </span>
                  {t.dashboard.recent}
                </h2>
                <Link to="/orgs/$orgId/sales/invoices" params={{ orgId }}>
                  {t.dashboard.viewAll}
                </Link>
              </div>
              <div className={styles.cardBody}>
                {recent.data?.items.length ? (
                  <ul className={styles.pickList} style={{ maxBlockSize: 'none' }}>
                    {recent.data.items.map((r) => {
                      const axis = taxAxisOf(r);
                      return (
                        <li key={r.id}>
                          <Link
                            to="/orgs/$orgId/sales/invoices/$docId"
                            params={{ orgId, docId: r.id }}
                            className={styles.pickRow}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <span className={styles.mono}>{r.number}</span>
                            <span>
                              <span className={styles.pickTitle}>
                                {r.cash ? t.sales.cash : (r.receiverName ?? t.sales.noCustomer)}
                              </span>
                              <span className={styles.subtle} style={{ display: 'block' }}>
                                {r.date ? formatDate(r.date) : ''}
                              </span>
                            </span>
                            <span className={styles.latestSide}>
                              <Money value={r.totalAmount} currency="EGP" />
                              {axis ? (
                                <TaxStatusBadge value={axis} />
                              ) : (
                                <DocStatusBadge value={r.status} />
                              )}
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className={styles.subtle}>{t.sales.latestEmpty}</p>
                )}
              </div>
            </section>
          ) : null}

          <Panel title={t.orgs.profileTitle} id="org-profile">
            {org.data ? (
              <DescriptionList
                items={[
                  { key: 'name', term: t.orgs.name, value: org.data.name },
                  {
                    key: 'legalName',
                    term: t.orgs.legalName,
                    value: org.data.legalName ?? <span>{t.common.notSet}</span>,
                  },
                  {
                    key: 'rin',
                    term: t.orgs.rin,
                    value: org.data.rin ? (
                      <Code>{org.data.rin}</Code>
                    ) : (
                      <span>{t.common.notSet}</span>
                    ),
                  },
                  {
                    key: 'email',
                    term: t.setup.email,
                    value: org.data.email ? (
                      <Code>{org.data.email}</Code>
                    ) : (
                      <span>{t.common.notSet}</span>
                    ),
                  },
                  {
                    key: 'phone',
                    term: t.setup.phone,
                    value: org.data.phone ? (
                      <Code>{org.data.phone}</Code>
                    ) : (
                      <span>{t.common.notSet}</span>
                    ),
                  },
                  {
                    key: 'currency',
                    term: t.orgs.baseCurrency,
                    value: <Code>{org.data.baseCurrency}</Code>,
                  },
                  {
                    key: 'currencies',
                    term: t.setup.currencyTitle,
                    value: (
                      <span
                        style={{
                          display: 'inline-flex',
                          gap: '0.5em',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                        }}
                      >
                        {(currencies.data ?? []).map((c) => (
                          <Code key={c.code}>{c.code}</Code>
                        ))}
                        {canSettings ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onPress={() => setDialog('currency')}
                            testId="setup-currency"
                          >
                            {t.setup.addCurrency}
                          </Button>
                        ) : null}
                      </span>
                    ),
                  },
                  {
                    key: 'environment',
                    term: t.orgs.environment,
                    value: (
                      <span style={{ display: 'inline-flex', gap: '0.4em', alignItems: 'center' }}>
                        <Lock size={14} aria-label={t.orgs.environmentLockedTitle} />
                        {t.env[org.data.etaEnvironment]}
                      </span>
                    ),
                  },
                ]}
              />
            ) : org.isPending ? (
              <Skeleton height="6rem" />
            ) : null}
          </Panel>
        </div>

        <aside className={styles.side}>
          <section className={styles.card} data-testid="setup-checklist">
            <div className={styles.cardHead}>
              <h2 className={styles.cardTitle}>
                <span className={styles.cardIcon} aria-hidden="true">
                  <CheckCircle2 size={16} />
                </span>
                {remaining === 0 ? t.dashboard.setupDone : t.dashboard.setupTitle}
              </h2>
            </div>
            <div className={styles.cardBody}>
              <p className={styles.note} style={{ marginBlockStart: 0 }}>
                {t.dashboard.setupBody}
              </p>
              <ul className={styles.issueList} style={{ marginBlockStart: 'var(--space-3)' }}>
                {steps.map((s) => {
                  const allowed =
                    s === 'customer'
                      ? access.can('customer.create')
                      : s === 'item'
                        ? access.can('item.create')
                        : canSettings;
                  return (
                    <li
                      key={s}
                      className={styles.pickRow}
                      style={{ gridTemplateColumns: 'auto 1fr auto', cursor: 'default' }}
                      data-testid={`setup-${s}`}
                      data-done={done[s]}
                    >
                      {done[s] ? (
                        <CheckCircle2 size={18} color="var(--status-valid)" aria-hidden="true" />
                      ) : (
                        <CircleDashed size={18} color="var(--status-pending)" aria-hidden="true" />
                      )}
                      <span>{t.dashboard.steps[s]}</span>
                      {done[s] && s !== 'tax' && s !== 'customer' && s !== 'item' ? (
                        <span className={styles.subtle}>{t.dashboard.stepDone}</span>
                      ) : allowed && !(s === 'activity' && done.activity) ? (
                        <Button
                          size="sm"
                          variant={done[s] ? 'ghost' : 'secondary'}
                          onPress={() => setDialog(s)}
                          testId={`setup-${s}-action`}
                        >
                          {t.dashboard.stepAction}
                        </Button>
                      ) : (
                        <span />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
        </aside>
      </div>

      <ProfileDialog
        orgId={orgId}
        isOpen={dialog === 'rin' || dialog === 'profile'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <BranchDialog
        orgId={orgId}
        isOpen={dialog === 'branch'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <ActivityDialog
        orgId={orgId}
        isOpen={dialog === 'activity'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <TaxDialog
        orgId={orgId}
        isOpen={dialog === 'tax'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <CurrencyDialog
        orgId={orgId}
        isOpen={dialog === 'currency'}
        onOpenChange={(o) => !o && setDialog(null)}
      />
      <CustomerDialog
        orgId={orgId}
        isOpen={dialog === 'customer'}
        onOpenChange={(o) => !o && setDialog(null)}
        onSaved={() => undefined}
      />
      <ItemDialog
        orgId={orgId}
        isOpen={dialog === 'item'}
        onOpenChange={(o) => !o && setDialog(null)}
        onSaved={() => undefined}
      />
    </Page>
  );
}

function Kpi({
  tone,
  icon,
  label,
  value,
  to,
  orgId,
  testId,
}: {
  tone: string | undefined;
  icon: ReactNode;
  label: string;
  value: number | undefined;
  to: { status?: string; taxStatus?: string };
  orgId: string;
  testId: string;
}) {
  const { formatNumber } = useI18n();
  return (
    <Link
      to="/orgs/$orgId/sales/invoices"
      params={{ orgId }}
      search={to}
      className={`${styles.kpi} ${tone ?? ''}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
      data-testid={testId}
    >
      <span className={styles.kpiLabel}>
        {icon}
        {label}
      </span>
      <span className={styles.kpiValue}>
        {value === undefined ? '—' : formatNumber(String(value))}
      </span>
    </Link>
  );
}
